import React, { useState } from "react";
import { Card, Collapse, Descriptions, Tag, Timeline, Table, Space, Alert, Divider, Typography, Tabs } from "antd";
import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined,
  InfoCircleOutlined,
  MailOutlined,
  ShoppingCartOutlined,
  DollarOutlined,
  FileTextOutlined,
  SplitCellsOutlined,
  PlusOutlined,
  ThunderboltOutlined,
  SendOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";

const { Panel } = Collapse;
const { Title, Paragraph, Text } = Typography;

const Documentation = () => {
  const { t } = useTranslation();
  const [activeKey, setActiveKey] = useState([]);
  const [activeTab, setActiveTab] = useState("purchase");

  const statusColors = {
    draft: "default",
    pending_confirmation: "gold",
    pending_approval: "volcano",
    pending_supplier_review: "gold",
    payment_pending: "orange",
    deposit_paid: "cyan",
    paid: "lime",
    ready_for_production: "geekblue",
    in_production: "blue",
    shipped: "orange",
    received: "cyan",
    under_review: "purple",
    completed: "green",
    cancelled: "red",
    split: "magenta",
  };

  const statusFlowData = [
    {
      status: "Draft",
      color: "default",
      description: "Order wordt samengesteld in het CMS",
      actions: "Producten toevoegen, leverancier selecteren, details invullen",
      nextStatus: "Pending Confirmation",
      trigger: "Bombyxx klikt op 'Set to Ordered'",
    },
    {
      status: "Pending Confirmation",
      color: "gold",
      description: "Wacht op bevestiging van leverancier",
      actions: "Leverancier vult verpakkingen en dimensies in",
      nextStatus: "Pending Approval",
      trigger: "Leverancier klikt op 'Confirm Order'",
    },
    {
      status: "Pending Approval",
      color: "volcano",
      description: "Bombyxx moet leverancier wijzigingen goedkeuren",
      actions: "Bombyxx controleert aantallen, prijzen en dimensies",
      nextStatus: "Ready for Production / Pending Supplier Review",
      trigger: "Bombyxx klikt op 'Approve Changes' of 'Save and Send to Supplier'",
    },
    {
      status: "Pending Supplier Review",
      color: "gold",
      description: "Leverancier moet Bombyxx aanpassingen reviewen",
      actions: "Leverancier controleert aangepaste aantallen",
      nextStatus: "Pending Approval",
      trigger: "Leverancier past aan en klikt op 'Confirm Order'",
    },
    {
      status: "Ready for Production",
      color: "geekblue",
      description: "Order is goedgekeurd, wacht op betaling",
      actions: "Bombyxx verwerkt betaling in CMS",
      nextStatus: "In Production",
      trigger: "Betaling status wordt 'Paid'",
    },
    {
      status: "In Production",
      color: "blue",
      description: "Leverancier is bezig met productie",
      actions: "Leverancier kan invoice uploaden, tracking code toevoegen",
      nextStatus: "Shipped",
      trigger: "Leverancier klikt op 'Mark as Shipped' of voegt tracking code toe",
    },
    {
      status: "Shipped",
      color: "orange",
      description: "Order is verzonden door leverancier",
      actions: "Wachten op levering bij Bombyxx",
      nextStatus: "Received",
      trigger: "Bombyxx klikt op 'Set to Received'",
    },
    {
      status: "Received",
      color: "cyan",
      description: "Order is ontvangen bij Bombyxx",
      actions: "Bombyxx telt producten en controleert kwaliteit",
      nextStatus: "Under Review",
      trigger: "Bombyxx klikt op 'Set to Under Review'",
    },
    {
      status: "Under Review",
      color: "purple",
      description: "Bombyxx reviewt ontvangen producten",
      actions: "Discrepanties registreren, kwaliteit controleren",
      nextStatus: "Completed",
      trigger: "Bombyxx klikt op 'Complete Order'",
    },
    {
      status: "Completed",
      color: "green",
      description: "Order is volledig afgerond",
      actions: "Geen acties meer mogelijk",
      nextStatus: "-",
      trigger: "-",
    },
  ];

  const emailNotifications = [
    {
      trigger: "PO Status: Draft → Pending Confirmation",
      recipient: "Leverancier",
      subject: "Action Required: Confirm Purchase Order",
      content: "Nieuwe order ontvangen, bevestig de order in het Supplier Portal",
      category: "purchase_order",
    },
    {
      trigger: "PO Status: Pending Confirmation → Pending Approval",
      recipient: "Bombyxx",
      subject: "Actie vereist: Leverancier wijzigingen",
      content: "Leverancier heeft order bevestigd, controleer en keur goed",
      category: "purchase_order",
    },
    {
      trigger: "PO Status: Pending Approval → Pending Supplier Review",
      recipient: "Leverancier",
      subject: "Bombyxx has made adjustments",
      content: "Bombyxx heeft aantallen aangepast, review de wijzigingen",
      category: "purchase_order",
    },
    {
      trigger: "PO Status: Ready for Production → In Production",
      recipient: "Bombyxx",
      subject: "Productie gestart",
      content: "Leverancier heeft productie gestart + Exact sync (order aanmaken)",
      category: "purchase_order",
    },
    {
      trigger: "Betaling status: Pending → Paid",
      recipient: "Leverancier",
      subject: "Payment Received",
      content: "Betaling is ontvangen, productie kan starten",
      category: "purchase_order",
    },
    {
      trigger: "PO Status: In Production → Shipped",
      recipient: "Bombyxx",
      subject: "Order verzonden",
      content: "Leverancier heeft order verzonden + pre-order logica voor producten zonder voorraad",
      category: "purchase_order",
    },
    {
      trigger: "Additional Costs toegevoegd",
      recipient: "Bombyxx",
      subject: "Extra kosten toegevoegd",
      content: "Leverancier heeft extra kosten toegevoegd aan de invoice",
      category: "purchase_order",
    },
    {
      trigger: "Shipment Status → Shipped / In Transit",
      recipient: "Bombyxx",
      subject: "Verzending {nr} is verzonden/onderweg",
      content: "Leverancier heeft verzending verzonden of is onderweg",
      category: "shipment",
    },
    {
      trigger: "Shipment Status → Received",
      recipient: "Leverancier + Bombyxx",
      subject: "Verzending {nr} is ontvangen",
      content: "Bombyxx heeft de verzending ontvangen, telling wordt uitgevoerd",
      category: "shipment",
    },
    {
      trigger: "Shipment Status → Completed",
      recipient: "Leverancier + Bombyxx",
      subject: "Verzending {nr} is geteld en afgerond",
      content: "Telling afgerond + Voorraadtelling (Stock Count) verwerkt in Exact via QuantityDifference",
      category: "shipment",
    },
    {
      trigger: "Na voorraadtelling: notify-accountant",
      recipient: "Boekhouder (Bombyxx intern)",
      subject: "Ontvangst verwerkt: Purchase Order {nr}",
      content: "Direct verstuurd door CMS via POST /api/exact/shipments/{id}/notify-accountant. Tijdstip zichtbaar als 'Mail boekhouder verstuurd' op shipment detail.",
      category: "shipment",
    },
    {
      trigger: "Invoice aangemaakt (onCreate)",
      recipient: "Bombyxx",
      subject: "Nieuwe factuur ontvangen: {nr}",
      content: "Leverancier heeft factuur ingediend + automatische sync naar Exact Online",
      category: "invoice",
    },
    {
      trigger: "Invoice Status → Approved",
      recipient: "Leverancier",
      subject: "Invoice {nr} has been approved",
      content: "Bombyxx heeft de factuur goedgekeurd",
      category: "invoice",
    },
    {
      trigger: "Invoice Status → Paid",
      recipient: "Leverancier",
      subject: "Invoice {nr} has been fully paid",
      content: "Factuur is volledig betaald",
      category: "invoice",
    },
    {
      trigger: "Invoice Status → Partially Paid",
      recipient: "Leverancier",
      subject: "Invoice {nr}: Partial payment received",
      content: "Gedeeltelijke betaling ontvangen voor factuur",
      category: "invoice",
    },
  ];

  const invoiceProcess = [
    {
      step: "1. Proforma Invoice (Optioneel, op PO-niveau)",
      description: "Leverancier kan een proforma invoice uploaden op de purchase order voordat productie start",
      fields: ["Proforma Invoice Number", "Proforma Invoice Date", "Proforma PDF"],
      timing: "Tijdens In Production status",
      location: "Supplier Portal - PO Detail - Invoice Modal - Proforma tab",
    },
    {
      step: "2. Verzending (Shipment) aanmaken",
      description: "Bombyxx maakt een verzending aan in het CMS die producten uit één of meerdere PO's bundelt",
      fields: ["Shipment Number", "Source Orders", "Items (per product: quantity)", "Tracking Code", "ETD"],
      timing: "Wanneer producten klaar zijn voor verzending",
      location: "CMS - Verzendingen",
    },
    {
      step: "3. Final Invoice (op verzending-niveau)",
      description: "Leverancier uploadt de definitieve factuur gekoppeld aan een verzending (niet meer aan PO)",
      fields: [
        "Invoice Number",
        "Invoice Date",
        "Invoice Lines (automatisch gegenereerd op basis van shipment items)",
        "Additional Costs (optioneel: mold costs, photoshoot, etc.)",
        "Remarks",
        "Invoice PDF",
      ],
      timing: "Na aanmaken verzending",
      location: "Supplier Portal - Shipment Detail - Invoice Modal",
    },
    {
      step: "4. Factuur Status",
      description: "Bombyxx beheert de factuurstatus in het CMS via de shipment detail pagina",
      fields: ["Draft", "Submitted (standaard bij aanmaken)", "Approved", "Partially Paid", "Paid", "Disputed"],
      timing: "Na ontvangst factuur",
      location: "CMS - Shipment Detail - Factuur sectie - Status dropdown",
    },
    {
      step: "5. Invoice Synchronisatie",
      description: "Invoice wordt automatisch gesynchroniseerd naar Exact Online",
      fields: ["Purchase Invoice ID wordt opgeslagen in Firestore (exactPurchaseInvoiceId)"],
      timing: "Automatisch via Cloud Function",
      location: "Exact Online - Purchase Invoices",
    },
  ];

  const splitOrderProcess = [
    {
      step: "1. Initiëren Split",
      description: "Leverancier opent de Split Order modal",
      location: "Supplier Portal - Order Detail - 'Split Order' button",
      requirement: "Order moet in 'In Production' status zijn",
    },
    {
      step: "2. Verdelen Quantities",
      description: "Leverancier verdeelt product quantities over twee groepen",
      validation: "Totaal moet exact gelijk zijn aan originele quantity per product",
      example: "Product A: 100 stuks → Split in 20 (Order A) + 80 (Order B)",
    },
    {
      step: "3. ETD Invullen",
      description: "Beide nieuwe orders krijgen eigen ETD (CBM wordt later per verzending bepaald)",
      fields: ["ETD Now (Order A)", "ETD Later (Order B)"],
      validation: "Alle velden zijn verplicht",
    },
    {
      step: "4. Invoice Aanpassing",
      description: "Invoice wordt automatisch proportioneel aangepast",
      proforma: "Blijft ongewijzigd bij beide orders",
      finalInvoice: "Line items worden proportioneel aangepast op basis van nieuwe quantities",
      additionalCosts: "Worden volledig toegewezen aan Order A (eerste order)",
      pdfRemoval: "Final invoice PDF wordt verwijderd (moet opnieuw geüpload worden)",
    },
    {
      step: "5. Nieuwe Orders",
      description: "Twee nieuwe child orders worden aangemaakt",
      orderA: "Krijgt suffix '-A', bevat 'Now' producten en ETD",
      orderB: "Krijgt suffix '-B', bevat 'Later' producten en ETD",
      originalOrder: "Status wordt 'Split', blijft zichtbaar met link naar child orders",
    },
  ];

  const paymentProcess = [
    {
      status: "Submitted (Ingediend)",
      description: "Factuur is aangemaakt door leverancier, wacht op betaling",
      action: "Bombyxx voegt betaling toe via 'Betaling toevoegen' op de shipment detail pagina",
      fields: ["Amount (USD)", "Payment Date", "Payment Method (bank, Wise, PayPal, etc.)", "Reference / Transaction ID", "Notes (optioneel)", "Payment Proof (PDF/afbeelding)"],
    },
    {
      status: "Partially Paid (Deels betaald)",
      description: "Eén of meer betalingen gedaan, maar nog niet volledig betaald",
      calculation: "0 < totalPaid < totalAmount",
      action: "Bombyxx kan aanvullende betalingen toevoegen",
    },
    {
      status: "Paid (Betaald)",
      description: "Factuur is volledig betaald",
      calculation: "totalPaid ≥ totalAmount",
      trigger: "Status wordt automatisch 'Paid' bij volledige betaling",
    },
  ];

  const productMasterDataUpdate = [
    {
      trigger: "Order Creation",
      description: "Bij het aanmaken van een nieuwe purchase order",
      updates: ["Purchase Price", "Inner Packaging Quantity", "Master Packaging Quantity"],
      condition: "Als waarden verschillen van huidige product master data",
    },
    {
      trigger: "Approve Supplier Changes",
      description: "Wanneer Bombyxx leverancier wijzigingen goedkeurt",
      updates: ["Purchase Price", "Inner Packaging Quantity", "Master Packaging Quantity"],
      condition: "Als adjusted values verschillen van originele values",
      location: "CMS - Order Detail - 'Approve Changes' button",
    },
  ];

  const productDraftProcess = [
    {
      step: "1. Product Draft Aanmaken",
      description: "Leverancier maakt een nieuw product draft aan in het Supplier Portal",
      location: "Supplier Portal - Order Detail - Product toevoegen",
      fields: ["Product Name", "Product Code", "Barcode (optioneel)"],
      status: "Needs Supplier Input",
    },
    {
      step: "2. Product Details Invullen",
      description: "Leverancier vult alle product details in",
      fields: [
        "Purchase Price",
        "Inner Packaging Quantity",
        "Master Packaging Quantity",
        "Length (optioneel)",
        "Width (optioneel)",
        "Height (optioneel)",
        "Diameter (optioneel)",
        "Color (optioneel)",
        "Product Images (optioneel)",
      ],
      action: "Leverancier klikt op 'Submit for Review'",
      status: "Submitted",
    },
    {
      step: "3. Bombyxx Review",
      description: "Bombyxx controleert de product details in het CMS",
      location: "CMS - Product Drafts pagina",
      options: ["Approve: Product wordt goedgekeurd", "Reject: Wijzigingen vereist met feedback"],
      status: "Submitted → Approved / Changes Requested",
    },
    {
      step: "4. Changes Requested (optioneel)",
      description: "Als Bombyxx wijzigingen vraagt, moet leverancier aanpassen",
      action: "Leverancier past product details aan en submit opnieuw",
      status: "Changes Requested → Submitted",
    },
    {
      step: "5. Product Publiceren",
      description: "Na approval kan Bombyxx het product publiceren naar de catalog",
      action: "Bombyxx klikt op 'Publish' in Product Drafts detail modal",
      result: "Nieuw product wordt aangemaakt in products collection",
      status: "Approved → Published",
    },
    {
      step: "6. Purchase Order Update",
      description: "Purchase order wordt automatisch geüpdatet met published product ID",
      result: "Product draft wordt gekoppeld aan het nieuwe product in de catalog",
    },
  ];

  const productDraftStatuses = [
    {
      status: "Needs Supplier Input",
      color: "default",
      description: "Product draft is aangemaakt maar nog niet compleet",
      action: "Leverancier moet details invullen",
    },
    {
      status: "Submitted",
      color: "processing",
      description: "Leverancier heeft product details ingediend",
      action: "Bombyxx moet reviewen en goedkeuren/afwijzen",
    },
    {
      status: "Changes Requested",
      color: "warning",
      description: "Bombyxx heeft wijzigingen gevraagd",
      action: "Leverancier moet aanpassingen maken",
    },
    {
      status: "Approved",
      color: "success",
      description: "Bombyxx heeft product goedgekeurd",
      action: "Bombyxx kan product publiceren naar catalog",
    },
    {
      status: "Published",
      color: "green",
      description: "Product is gepubliceerd naar de catalog",
      action: "Geen acties meer mogelijk",
    },
  ];

  const cloudFunctions = [
    {
      name: "onUpdatePurchaseOrder",
      collection: "purchaseOrders",
      event: "onUpdate",
      description: "Reageert op status changes van purchase orders",
      actions: [
        "Email naar leverancier bij: pending_confirmation, paid, cancelled",
        "Email naar Bombyxx bij: pending_approval, payment_pending, in_production, shipped",
        "Additional costs notificatie naar Bombyxx",
        "Pre-order logica bij shipped: producten zonder voorraad op pre-order zetten",
        "Exact sync bij in_production: purchase order aanmaken in Exact Online",
      ],
    },
    {
      name: "onUpdatePurchaseOrderShipment",
      collection: "purchaseOrderShipments",
      event: "onUpdate",
      description: "Reageert op status changes van verzendingen",
      actions: [
        "Email naar Bombyxx bij: shipped, in_transit",
        "Email naar leverancier + Bombyxx bij: received",
        "Email naar leverancier + Bombyxx bij: completed",
        "Geen Exact sync via Cloud Function — voorraadtelling wordt direct via CMS → API verwerkt",
      ],
    },
    {
      name: "onCreatePurchaseOrderInvoice",
      collection: "purchaseOrderInvoices",
      event: "onCreate",
      description: "Reageert op nieuwe facturen",
      actions: [
        "Sync factuur naar Exact Online (Purchase Invoice aanmaken)",
        "Slaat exactPurchaseInvoiceId op in Firestore",
        "Email notificatie naar Bombyxx: nieuwe factuur ontvangen",
      ],
    },
    {
      name: "onUpdatePurchaseOrderInvoice",
      collection: "purchaseOrderInvoices",
      event: "onUpdate",
      description: "Reageert op factuur status changes",
      actions: [
        "Email naar leverancier bij: approved, paid, partially_paid",
      ],
    },
  ];

  const shipmentProcess = [
    {
      step: "1. Verzending Aanmaken",
      description: "Leverancier maakt een verzending aan in het Supplier Portal, met producten uit één of meerdere purchase orders",
      fields: ["Shipment Number (automatisch)", "Source Orders", "Items per product: quantity", "Tracking Code (optioneel)", "ETD (optioneel)"],
      timing: "Wanneer producten klaar zijn voor verzending",
      location: "Supplier Portal - Shipments - Create Shipment",
    },
    {
      step: "2. Verzending Onderweg",
      description: "Verzending wordt verzonden door leverancier. Status gaat naar 'shipped' of 'in_transit'",
      timing: "Na aanmaken verzending",
      location: "Supplier Portal / CMS - Shipment Detail",
    },
    {
      step: "3. Verzending Ontvangen",
      description: "Bombyxx ontvangt de verzending en zet status op 'received'",
      timing: "Bij fysieke ontvangst",
      location: "CMS - Shipment Detail - Status dropdown",
    },
    {
      step: "4. Kostprijs Controle (voor vreemde valuta)",
      description: "Voordat de telling begint, verschijnt automatisch een kostprijs controle modal als de verzending producten bevat met een vreemde valuta (USD, IDR, etc.)",
      fields: [
        "Wisselkoers (automatisch opgehaald uit purchase order, aanpasbaar)",
        "Per product: Inkoopprijs origineel, Nieuwe kostprijs EUR (automatisch berekend, aanpasbaar)",
        "Formule: Inkoopprijs × Wisselkoers = Kostprijs EUR"
      ],
      timing: "Bij openen 'Verwerk Ontvangst' modal",
      location: "CMS - Shipment Detail - Kostprijs modal (automatisch)",
      notification: "Na bevestiging worden de kostprijzen bijgewerkt in Exact Online via CostPriceNew op het logistics/Items endpoint (niet SupplierItem). History record wordt opgeslagen.",
    },
    {
      step: "5. Voorraadtelling (Stock Count)",
      description: "Bombyxx telt de ontvangen producten via de 'Verwerk Ontvangst' modal. De voorraad wordt verhoogd met de ontvangen aantallen via QuantityDifference — de bestaande voorraad wordt niet overschreven.",
      fields: ["Per product: Geteld/ontvangen aantal (editable input)", "Besteld aantal (read-only ter referentie)"],
      timing: "Na kostprijs controle (of direct bij EUR orders)",
      location: "CMS - Shipment Detail - '🔢 Voorraadtelling verwerken' knop",
    },
    {
      step: "6. Verwerking na Telling",
      description: "Na een succesvolle voorraadtelling worden de volgende acties direct door de CMS uitgevoerd (niet via Cloud Function):",
      fields: [
        "Stock count aangemaakt + verwerkt in Exact Online (QuantityDifference per artikel)",
        "exactStockCountId (Exact GUID) opgeslagen in Firestore op de shipment",
        "Shipment status → completed, receivedAt + receivedQuantities opgeslagen",
        "Mail naar boekhouder verstuurd via POST /api/exact/shipments/{id}/notify-accountant",
        "accountantMailSentAt (Unix timestamp) opgeslagen in Firestore op de shipment",
        "Email naar leverancier + Bombyxx via Cloud Function onUpdatePurchaseOrderShipment",
      ],
      timing: "Direct na klikken op '🔢 Voorraadtelling verwerken'",
      location: "CMS → Java Spring Boot API → Exact Online",
    },
  ];

  const columns = [
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (text, record) => <Tag color={record.color}>{text}</Tag>,
    },
    {
      title: "Beschrijving",
      dataIndex: "description",
      key: "description",
    },
    {
      title: "Acties",
      dataIndex: "actions",
      key: "actions",
    },
    {
      title: "Volgende Status",
      dataIndex: "nextStatus",
      key: "nextStatus",
    },
    {
      title: "Trigger",
      dataIndex: "trigger",
      key: "trigger",
    },
  ];

  const emailColumns = [
    {
      title: "Bron",
      dataIndex: "category",
      key: "category",
      width: 120,
      render: (text) => {
        const colors = { purchase_order: "blue", shipment: "orange", invoice: "purple" };
        const labels = { purchase_order: "Purchase Order", shipment: "Verzending", invoice: "Factuur" };
        return <Tag color={colors[text] || "default"}>{labels[text] || text}</Tag>;
      },
    },
    {
      title: "Trigger",
      dataIndex: "trigger",
      key: "trigger",
    },
    {
      title: "Ontvanger",
      dataIndex: "recipient",
      key: "recipient",
      render: (text) => {
        if (text.includes("+")) {
          return text.split("+").map((r) => r.trim()).map((r, i) => (
            <Tag key={i} color={r === "Bombyxx" ? "blue" : "green"} style={{ marginBottom: 2 }}>{r}</Tag>
          ));
        }
        return <Tag color={text === "Bombyxx" ? "blue" : "green"}>{text}</Tag>;
      },
    },
    {
      title: "Subject",
      dataIndex: "subject",
      key: "subject",
    },
    {
      title: "Inhoud",
      dataIndex: "content",
      key: "content",
    },
  ];

  return (
    <Card>
      <Title level={2}>
        <FileTextOutlined /> Documentatie
      </Title>
      <Paragraph>
        Volledige documentatie voor het Bombyxx systeem, inclusief inkoopproces en marketing functionaliteiten.
      </Paragraph>

      <Tabs 
        activeKey={activeTab} 
        onChange={setActiveTab}
        style={{ marginTop: 24 }}
        items={[
          {
            key: "purchase",
            label: (
              <span>
                <ShoppingCartOutlined /> Inkoopproces
              </span>
            ),
            children: (
              <>
                <Paragraph>
                  Deze documentatie beschrijft het volledige inkoopproces van Bombyxx, inclusief alle statussen, workflows, email
                  notificaties en speciale processen zoals facturatie en order splitting.
                </Paragraph>
                <Collapse activeKey={activeKey} onChange={setActiveKey}>
        {/* Status Flow */}
        <Panel
          header={
            <Space>
              <ShoppingCartOutlined />
              <strong>1. Purchase Order Status Flow</strong>
            </Space>
          }
          key="1"
        >
          <Alert
            message="Complete Status Flow"
            description="Dit is de normale flow van een purchase order van begin tot eind. Sommige statussen kunnen worden overgeslagen afhankelijk van het proces."
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
          />

          <Table
            dataSource={statusFlowData}
            columns={columns}
            pagination={false}
            rowKey="status"
            size="small"
            bordered
          />

          <Divider />

          <Title level={4}>Visuele Timeline</Title>
          <Timeline mode="left">
            <Timeline.Item color="gray" dot={<ClockCircleOutlined />}>
              <strong>Draft</strong>
              <br />
              Order wordt samengesteld in CMS
            </Timeline.Item>
            <Timeline.Item color="gold" dot={<ExclamationCircleOutlined />}>
              <strong>Pending Confirmation</strong>
              <br />
              Leverancier vult product details in (ETD en CBM worden per verzending bepaald)
            </Timeline.Item>
            <Timeline.Item color="volcano" dot={<ExclamationCircleOutlined />}>
              <strong>Pending Approval</strong>
              <br />
              Bombyxx controleert en keurt goed
            </Timeline.Item>
            <Timeline.Item color="geekblue" dot={<DollarOutlined />}>
              <strong>Ready for Production</strong>
              <br />
              Wacht op betaling
            </Timeline.Item>
            <Timeline.Item color="blue" dot={<ClockCircleOutlined />}>
              <strong>In Production</strong>
              <br />
              Leverancier produceert
            </Timeline.Item>
            <Timeline.Item color="orange" dot={<InfoCircleOutlined />}>
              <strong>Shipped</strong>
              <br />
              Order is verzonden
            </Timeline.Item>
            <Timeline.Item color="cyan" dot={<CheckCircleOutlined />}>
              <strong>Received</strong>
              <br />
              Order is ontvangen bij Bombyxx
            </Timeline.Item>
            <Timeline.Item color="purple" dot={<ClockCircleOutlined />}>
              <strong>Under Review</strong>
              <br />
              Bombyxx controleert kwaliteit
            </Timeline.Item>
            <Timeline.Item color="green" dot={<CheckCircleOutlined />}>
              <strong>Completed</strong>
              <br />
              Order is afgerond
            </Timeline.Item>
          </Timeline>

          <Divider />

          <Title level={4}>Alternatieve Flows</Title>
          <Descriptions bordered column={1} size="small">
            <Descriptions.Item label="Pending Approval → Pending Supplier Review">
              Bombyxx past aantallen aan en stuurt terug naar leverancier
            </Descriptions.Item>
            <Descriptions.Item label="Pending Supplier Review → Pending Approval">
              Leverancier past aan en stuurt terug naar Bombyxx voor goedkeuring
            </Descriptions.Item>
            <Descriptions.Item label="In Production → Split">
              Leverancier splitst order in twee aparte orders met verschillende ETD's
            </Descriptions.Item>
            <Descriptions.Item label="Elke status → Cancelled">
              Order kan op elk moment worden geannuleerd (behalve Completed)
            </Descriptions.Item>
          </Descriptions>
        </Panel>

        {/* Email Notifications */}
        <Panel
          header={
            <Space>
              <MailOutlined />
              <strong>2. Email Notificaties</strong>
            </Space>
          }
          key="2"
        >
          <Alert
            message="Automatische Email Notificaties"
            description="Alle emails worden automatisch verstuurd via Cloud Functions wanneer bepaalde triggers plaatsvinden. Emails worden in een queue geplaatst en binnen 1 minuut verzonden."
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
          />

          <Table
            dataSource={emailNotifications}
            columns={emailColumns}
            pagination={false}
            rowKey="trigger"
            size="small"
            bordered
          />

          <Divider />

          <Title level={4}>Email Queue Systeem</Title>
          <Descriptions bordered column={1} size="small">
            <Descriptions.Item label="Collection">emailQueue (Firestore)</Descriptions.Item>
            <Descriptions.Item label="Processing">Cloud Function checkt elke minuut op nieuwe emails</Descriptions.Item>
            <Descriptions.Item label="Status">scheduled → sent / failed</Descriptions.Item>
            <Descriptions.Item label="Retry Logic">Max 3 retries bij failures, 1 uur tussen retries</Descriptions.Item>
            <Descriptions.Item label="Email Service">Java Spring Boot API (MarketingService)</Descriptions.Item>
          </Descriptions>
        </Panel>

        {/* Invoice Process */}
        <Panel
          header={
            <Space>
              <FileTextOutlined />
              <strong>3. Facturatie Proces</strong>
            </Space>
          }
          key="3"
        >
          <Alert
            message="Invoice Workflow"
            description="Leveranciers kunnen zowel een proforma invoice als een final invoice uploaden. Additional costs kunnen worden toegevoegd voor extra kosten zoals mold costs of photoshoot kosten."
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
          />

          <Timeline>
            {invoiceProcess.map((item, index) => (
              <Timeline.Item key={index} color="blue">
                <Title level={5}>{item.step}</Title>
                <Paragraph>{item.description}</Paragraph>
                {item.fields && (
                  <>
                    <Text strong>Velden:</Text>
                    <ul>
                      {item.fields.map((field, idx) => (
                        <li key={idx}>{field}</li>
                      ))}
                    </ul>
                  </>
                )}
                {item.timing && (
                  <Paragraph>
                    <Text strong>Timing:</Text> {item.timing}
                  </Paragraph>
                )}
                {item.location && (
                  <Paragraph>
                    <Text strong>Locatie:</Text> {item.location}
                  </Paragraph>
                )}
                {item.notification && (
                  <Alert message={item.notification} type="warning" showIcon style={{ marginTop: 8 }} />
                )}
              </Timeline.Item>
            ))}
          </Timeline>

          <Divider />

          <Title level={4}>Invoice Line Items</Title>
          <Descriptions bordered column={1} size="small">
            <Descriptions.Item label="Automatische Generatie">
              Invoice lines worden automatisch gegenereerd op basis van shipment items (producten in de verzending)
            </Descriptions.Item>
            <Descriptions.Item label="Velden per Line">
              Product ID, Product Name, Color, Quantity, Unit Price, Discount %, Total Price
            </Descriptions.Item>
            <Descriptions.Item label="Berekening">
              Total Price = Quantity × Unit Price × (1 - Discount% / 100)
            </Descriptions.Item>
            <Descriptions.Item label="Collectie">
              purchaseOrderInvoices (standalone document, gekoppeld aan shipmentId)
            </Descriptions.Item>
            <Descriptions.Item label="Factuuroverzicht">
              CMS - Inkoop - Inkoopfacturen: tabel met alle facturen, filters op leverancier/status/datum, kolommen voor status/betaald/openstaand
            </Descriptions.Item>
          </Descriptions>

          <Divider />

          <Title level={4}>Additional Costs</Title>
          <Descriptions bordered column={1} size="small">
            <Descriptions.Item label="Voorbeelden">
              Mold costs, Photoshoot kosten, Sample kosten, Shipping kosten
            </Descriptions.Item>
            <Descriptions.Item label="Velden">
              Description, Amount, Currency, Quantity (optioneel), Unit Price (optioneel), Remarks
            </Descriptions.Item>
            <Descriptions.Item label="Email Notificatie">
              Bombyxx ontvangt automatisch een email wanneer additional costs worden toegevoegd
            </Descriptions.Item>
            <Descriptions.Item label="Bij Order Split">
              Alle additional costs worden toegewezen aan de eerste child order (Order A)
            </Descriptions.Item>
            <Descriptions.Item label="Weergave in CMS">
              Total Price toont breakdown: Producten + Extra Kosten = Totaal
            </Descriptions.Item>
          </Descriptions>

          <Divider />

          <Title level={4}>Invoice Synchronisatie naar Exact Online</Title>
          <Descriptions bordered column={1} size="small">
            <Descriptions.Item label="Trigger">
              Automatisch via Cloud Function wanneer invoice wordt toegevoegd/geüpdatet
            </Descriptions.Item>
            <Descriptions.Item label="API Endpoint">POST /api/exact/purchase_order/invoice</Descriptions.Item>
            <Descriptions.Item label="Exact Purchase Invoice">
              Wordt aangemaakt in Exact Online met alle invoice lines en additional costs
            </Descriptions.Item>
            <Descriptions.Item label="Exact ID">
              Purchase Invoice ID wordt opgeslagen in Firestore (exactPurchaseInvoiceId)
            </Descriptions.Item>
            <Descriptions.Item label="Status Indicator">
              CMS toont "✓ Synced to Exact" tag wanneer synchronisatie succesvol is
            </Descriptions.Item>
          </Descriptions>
        </Panel>

        {/* Order Splitting */}
        <Panel
          header={
            <Space>
              <SplitCellsOutlined />
              <strong>4. Order Splitting</strong>
            </Space>
          }
          key="4"
        >
          <Alert
            message="Order Split Functionaliteit"
            description="Leveranciers kunnen een order splitsen in twee aparte orders met verschillende ETD's. Dit is handig wanneer producten op verschillende momenten klaar zijn."
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
          />

          <Timeline>
            {splitOrderProcess.map((item, index) => (
              <Timeline.Item key={index} color="purple">
                <Title level={5}>{item.step}</Title>
                <Paragraph>{item.description}</Paragraph>
                {item.location && (
                  <Paragraph>
                    <Text strong>Locatie:</Text> {item.location}
                  </Paragraph>
                )}
                {item.requirement && (
                  <Alert message={item.requirement} type="warning" showIcon style={{ marginTop: 8 }} />
                )}
                {item.validation && (
                  <Paragraph>
                    <Text strong>Validatie:</Text> {item.validation}
                  </Paragraph>
                )}
                {item.example && (
                  <Paragraph>
                    <Text strong>Voorbeeld:</Text> {item.example}
                  </Paragraph>
                )}
                {item.fields && (
                  <>
                    <Text strong>Velden:</Text>
                    <ul>
                      {item.fields.map((field, idx) => (
                        <li key={idx}>{field}</li>
                      ))}
                    </ul>
                  </>
                )}
                {item.proforma && (
                  <Paragraph>
                    <Text strong>Proforma Invoice:</Text> {item.proforma}
                  </Paragraph>
                )}
                {item.finalInvoice && (
                  <Paragraph>
                    <Text strong>Final Invoice:</Text> {item.finalInvoice}
                  </Paragraph>
                )}
                {item.additionalCosts && (
                  <Paragraph>
                    <Text strong>Additional Costs:</Text> {item.additionalCosts}
                  </Paragraph>
                )}
                {item.pdfRemoval && (
                  <Alert message={item.pdfRemoval} type="warning" showIcon style={{ marginTop: 8 }} />
                )}
                {item.orderA && (
                  <Paragraph>
                    <Text strong>Order A:</Text> {item.orderA}
                  </Paragraph>
                )}
                {item.orderB && (
                  <Paragraph>
                    <Text strong>Order B:</Text> {item.orderB}
                  </Paragraph>
                )}
                {item.originalOrder && (
                  <Paragraph>
                    <Text strong>Originele Order:</Text> {item.originalOrder}
                  </Paragraph>
                )}
              </Timeline.Item>
            ))}
          </Timeline>

          <Divider />

          <Title level={4}>Voorbeeld Split Scenario</Title>
          <Card size="small" style={{ backgroundColor: "#f0f5ff" }}>
            <Paragraph>
              <Text strong>Originele Order:</Text> IO-20260122-001
            </Paragraph>
            <ul>
              <li>Product A: 100 stuks @ $10 = $1,000</li>
              <li>Product B: 50 stuks @ $20 = $1,000</li>
              <li>Additional Costs: $200 (Mold costs)</li>
              <li>Total: $2,200</li>
            </ul>

            <Divider />

            <Paragraph>
              <Text strong>Na Split:</Text>
            </Paragraph>

            <Paragraph>
              <Text strong>Order A (IO-20260122-001-A):</Text>
            </Paragraph>
            <ul>
              <li>Product A: 20 stuks @ $10 = $200</li>
              <li>Product B: 10 stuks @ $20 = $200</li>
              <li>Additional Costs: $200 (Mold costs)</li>
              <li>Total: $600</li>
              <li>ETD: 2026-02-15</li>
            </ul>

            <Paragraph>
              <Text strong>Order B (IO-20260122-001-B):</Text>
            </Paragraph>
            <ul>
              <li>Product A: 80 stuks @ $10 = $800</li>
              <li>Product B: 40 stuks @ $20 = $800</li>
              <li>Additional Costs: $0</li>
              <li>Total: $1,600</li>
              <li>ETD: 2026-03-15</li>
            </ul>
          </Card>
        </Panel>

        {/* Payment Process */}
        <Panel
          header={
            <Space>
              <DollarOutlined />
              <strong>5. Betalingsproces</strong>
            </Space>
          }
          key="5"
        >
          <Alert
            message="Payment Status Systeem"
            description="Betalingen worden apart bijgehouden van de order status. Een order kan meerdere betalingen hebben (aanbetaling + restbetaling)."
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
          />

          <Timeline>
            {paymentProcess.map((item, index) => (
              <Timeline.Item key={index} color="green">
                <Title level={5}>{item.status}</Title>
                <Paragraph>{item.description}</Paragraph>
                {item.action && (
                  <Paragraph>
                    <Text strong>Actie:</Text> {item.action}
                  </Paragraph>
                )}
                {item.fields && (
                  <>
                    <Text strong>Velden:</Text>
                    <ul>
                      {item.fields.map((field, idx) => (
                        <li key={idx}>{field}</li>
                      ))}
                    </ul>
                  </>
                )}
                {item.calculation && (
                  <Paragraph>
                    <Text strong>Berekening:</Text> {item.calculation}
                  </Paragraph>
                )}
                {item.trigger && <Alert message={item.trigger} type="success" showIcon style={{ marginTop: 8 }} />}
              </Timeline.Item>
            ))}
          </Timeline>

          <Divider />

          <Title level={4}>Payment Berekeningen</Title>
          <Descriptions bordered column={1} size="small">
            <Descriptions.Item label="Collectie">
              purchaseOrderPayments (apart document per betaling, gekoppeld aan invoiceId)
            </Descriptions.Item>
            <Descriptions.Item label="Total to Pay">
              Invoice totalAmount (producten + additional costs)
            </Descriptions.Item>
            <Descriptions.Item label="Total Paid">
              Som van alle payment amounts (opgeslagen als totalPaid op de factuur)
            </Descriptions.Item>
            <Descriptions.Item label="Amount Due">
              totalAmount - totalPaid (opgeslagen als amountDue op de factuur)
            </Descriptions.Item>
            <Descriptions.Item label="Automatische Status Logic">
              <ul style={{ marginBottom: 0, paddingLeft: 20 }}>
                <li>Submitted: Geen betalingen (totalPaid = 0)</li>
                <li>Partially Paid: 0 &lt; totalPaid &lt; totalAmount</li>
                <li>Paid: totalPaid ≥ totalAmount</li>
              </ul>
            </Descriptions.Item>
            <Descriptions.Item label="Locatie">
              CMS - Shipment Detail - Factuur sectie - 'Betaling toevoegen' knop
            </Descriptions.Item>
            <Descriptions.Item label="Supplier Portal">
              Leverancier ziet betalingen read-only op de shipment detail pagina
            </Descriptions.Item>
          </Descriptions>

          <Divider />

          <Title level={4}>Factuur Status Overzicht</Title>
          <Descriptions bordered column={1} size="small">
            <Descriptions.Item label="Draft">Concept factuur, nog niet ingediend</Descriptions.Item>
            <Descriptions.Item label="Submitted">Ingediend door leverancier (standaard bij aanmaken)</Descriptions.Item>
            <Descriptions.Item label="Approved">Goedgekeurd door Bombyxx</Descriptions.Item>
            <Descriptions.Item label="Partially Paid">Deels betaald (automatisch bij eerste betaling)</Descriptions.Item>
            <Descriptions.Item label="Paid">Volledig betaald (automatisch wanneer totalPaid ≥ totalAmount)</Descriptions.Item>
            <Descriptions.Item label="Disputed">Betwist door Bombyxx</Descriptions.Item>
          </Descriptions>
        </Panel>

        {/* Product Master Data */}
        <Panel
          header={
            <Space>
              <InfoCircleOutlined />
              <strong>6. Product Master Data Updates</strong>
            </Space>
          }
          key="6"
        >
          <Alert
            message="Automatische Product Updates"
            description="Wanneer leveranciers prijzen of verpakkingen wijzigen in een purchase order, worden deze automatisch doorgevoerd in de product master data."
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
          />

          <Timeline>
            {productMasterDataUpdate.map((item, index) => (
              <Timeline.Item key={index} color="cyan">
                <Title level={5}>{item.trigger}</Title>
                <Paragraph>{item.description}</Paragraph>
                <Paragraph>
                  <Text strong>Updates:</Text>
                </Paragraph>
                <ul>
                  {item.updates.map((update, idx) => (
                    <li key={idx}>{update}</li>
                  ))}
                </ul>
                <Paragraph>
                  <Text strong>Conditie:</Text> {item.condition}
                </Paragraph>
                {item.location && (
                  <Paragraph>
                    <Text strong>Locatie:</Text> {item.location}
                  </Paragraph>
                )}
              </Timeline.Item>
            ))}
          </Timeline>

          <Divider />

          <Title level={4}>Update Logica</Title>
          <Descriptions bordered column={1} size="small">
            <Descriptions.Item label="Vergelijking">
              Order product waarden worden vergeleken met huidige product master data
            </Descriptions.Item>
            <Descriptions.Item label="Update Trigger">
              Alleen als waarden verschillen wordt product master data geüpdatet
            </Descriptions.Item>
            <Descriptions.Item label="Metadata">
              updatedAt (timestamp) en updatedBy (email) worden toegevoegd
            </Descriptions.Item>
            <Descriptions.Item label="Firestore Collection">products</Descriptions.Item>
          </Descriptions>

          <Divider />

          <Title level={4}>Voorbeeld Scenario</Title>
          <Card size="small" style={{ backgroundColor: "#f0f5ff" }}>
            <Paragraph>
              <Text strong>Product Master Data (voor order):</Text>
            </Paragraph>
            <ul>
              <li>Purchase Price: $10.00</li>
              <li>Inner Packaging: 12 stuks</li>
              <li>Master Packaging: 144 stuks</li>
            </ul>

            <Divider />

            <Paragraph>
              <Text strong>Leverancier wijzigt in order:</Text>
            </Paragraph>
            <ul>
              <li>Adjusted Price: $9.50</li>
              <li>Adjusted Inner Packaging: 10 stuks</li>
              <li>Adjusted Master Packaging: 120 stuks</li>
            </ul>

            <Divider />

            <Paragraph>
              <Text strong>Na Bombyxx Approval:</Text>
            </Paragraph>
            <ul>
              <li>Purchase Price: $9.50 ✓ Updated</li>
              <li>Inner Packaging: 10 stuks ✓ Updated</li>
              <li>Master Packaging: 120 stuks ✓ Updated</li>
              <li>updatedAt: 1737554400</li>
              <li>updatedBy: sam@digitalkiss.nl</li>
            </ul>
          </Card>
        </Panel>

        {/* Product Drafts */}
        <Panel
          header={
            <Space>
              <PlusOutlined />
              <strong>7. Nieuwe Producten Toevoegen (Product Drafts)</strong>
            </Space>
          }
          key="7"
        >
          <Alert
            message="Product Draft Workflow"
            description="Wanneer een leverancier een nieuw product wil toevoegen dat nog niet in de catalog staat, wordt een Product Draft aangemaakt. Dit doorloopt een goedkeuringsproces voordat het wordt gepubliceerd."
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
          />

          <Timeline>
            {productDraftProcess.map((item, index) => (
              <Timeline.Item key={index} color="blue">
                <Title level={5}>{item.step}</Title>
                <Paragraph>{item.description}</Paragraph>
                {item.location && (
                  <Paragraph>
                    <Text strong>Locatie:</Text> {item.location}
                  </Paragraph>
                )}
                {item.fields && (
                  <>
                    <Text strong>Velden:</Text>
                    <ul>
                      {item.fields.map((field, idx) => (
                        <li key={idx}>{field}</li>
                      ))}
                    </ul>
                  </>
                )}
                {item.options && (
                  <>
                    <Text strong>Opties:</Text>
                    <ul>
                      {item.options.map((option, idx) => (
                        <li key={idx}>{option}</li>
                      ))}
                    </ul>
                  </>
                )}
                {item.action && (
                  <Paragraph>
                    <Text strong>Actie:</Text> {item.action}
                  </Paragraph>
                )}
                {item.result && (
                  <Paragraph>
                    <Text strong>Resultaat:</Text> {item.result}
                  </Paragraph>
                )}
                {item.status && (
                  <Tag color="blue" style={{ marginTop: 8 }}>
                    {item.status}
                  </Tag>
                )}
              </Timeline.Item>
            ))}
          </Timeline>

          <Divider />

          <Title level={4}>Product Draft Statussen</Title>
          <Table
            dataSource={productDraftStatuses}
            columns={[
              {
                title: "Status",
                dataIndex: "status",
                key: "status",
                render: (text, record) => <Tag color={record.color}>{text}</Tag>,
              },
              {
                title: "Beschrijving",
                dataIndex: "description",
                key: "description",
              },
              {
                title: "Actie",
                dataIndex: "action",
                key: "action",
              },
            ]}
            pagination={false}
            rowKey="status"
            size="small"
            bordered
          />

          <Divider />

          <Title level={4}>Belangrijke Details</Title>
          <Descriptions bordered column={1} size="small">
            <Descriptions.Item label="Firestore Collection">productDrafts</Descriptions.Item>
            <Descriptions.Item label="Revisions">
              Elke status wijziging wordt opgeslagen in een revisions subcollection
            </Descriptions.Item>
            <Descriptions.Item label="Purchase Order Koppeling">
              Product draft wordt gekoppeld aan purchase order via purchaseOrderId en purchaseOrderProductIndex
            </Descriptions.Item>
            <Descriptions.Item label="Published Product">
              Na publicatie wordt publishedProductId opgeslagen in de draft
            </Descriptions.Item>
            <Descriptions.Item label="Existing Product Updates">
              Als een draft een publishedProductId heeft, worden wijzigingen doorgevoerd in het bestaande product
            </Descriptions.Item>
          </Descriptions>

          <Divider />

          <Title level={4}>Voorbeeld Scenario</Title>
          <Card size="small" style={{ backgroundColor: "#f0f5ff" }}>
            <Paragraph>
              <Text strong>Stap 1: Leverancier maakt nieuw product aan</Text>
            </Paragraph>
            <ul>
              <li>Product Name: "Bamboo Water Bottle"</li>
              <li>Product Code: "BWB-001"</li>
              <li>Barcode: "8719327001234"</li>
              <li>Status: Needs Supplier Input</li>
            </ul>

            <Divider />

            <Paragraph>
              <Text strong>Stap 2: Leverancier vult details in</Text>
            </Paragraph>
            <ul>
              <li>Purchase Price: $8.50</li>
              <li>Inner Packaging: 12 stuks</li>
              <li>Master Packaging: 144 stuks</li>
              <li>Length: 25 cm</li>
              <li>Diameter: 7 cm</li>
              <li>Color: Natural Bamboo</li>
              <li>Images: 3 product foto's geüpload</li>
              <li>Status: Submitted</li>
            </ul>

            <Divider />

            <Paragraph>
              <Text strong>Stap 3: Bombyxx keurt goed</Text>
            </Paragraph>
            <ul>
              <li>Reviewed by: sam@digitalkiss.nl</li>
              <li>Status: Approved</li>
            </ul>

            <Divider />

            <Paragraph>
              <Text strong>Stap 4: Bombyxx publiceert</Text>
            </Paragraph>
            <ul>
              <li>Nieuw product aangemaakt in products collection</li>
              <li>Product ID: abc123xyz</li>
              <li>Purchase order geüpdatet met productId</li>
              <li>Status: Published</li>
            </ul>
          </Card>
        </Panel>

        {/* Shipment & Goods Receipt Process */}
        <Panel
          header={
            <Space>
              <SendOutlined />
              <strong>8. Verzendingen & Goods Receipt (Tellingen)</strong>
            </Space>
          }
          key="8"
        >
          <Alert
            message="Verzendingen & Tellingen"
            description="Verzendingen zijn losgekoppeld van purchase orders. Een verzending kan producten uit meerdere PO's bevatten. Na ontvangst worden producten geteld en automatisch verwerkt in Exact Online."
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
          />

          <Timeline>
            {shipmentProcess.map((item, index) => (
              <Timeline.Item key={index} color={index === shipmentProcess.length - 1 ? "green" : "blue"}>
                <Title level={5}>{item.step}</Title>
                <Paragraph>{item.description}</Paragraph>
                {item.fields && (
                  <>
                    <Text strong>Velden / Acties:</Text>
                    <ul>
                      {item.fields.map((field, idx) => (
                        <li key={idx}>{field}</li>
                      ))}
                    </ul>
                  </>
                )}
                {item.timing && (
                  <Paragraph>
                    <Text strong>Timing:</Text> {item.timing}
                  </Paragraph>
                )}
                {item.location && (
                  <Paragraph>
                    <Text strong>Locatie:</Text> {item.location}
                  </Paragraph>
                )}
              </Timeline.Item>
            ))}
          </Timeline>

          <Divider />

          <Title level={4}>Shipment Statussen</Title>
          <Descriptions bordered column={1} size="small">
            <Descriptions.Item label={<Tag color="default">Draft</Tag>}>Verzending is aangemaakt maar nog niet verzonden</Descriptions.Item>
            <Descriptions.Item label={<Tag color="orange">Shipped</Tag>}>Verzending is verzonden door leverancier</Descriptions.Item>
            <Descriptions.Item label={<Tag color="blue">In Transit</Tag>}>Verzending is onderweg</Descriptions.Item>
            <Descriptions.Item label={<Tag color="cyan">Received</Tag>}>Verzending is ontvangen bij Bombyxx - klaar voor telling</Descriptions.Item>
            <Descriptions.Item label={<Tag color="green">Completed</Tag>}>Telling afgerond, voorraadtelling (stock count) verwerkt in Exact via QuantityDifference</Descriptions.Item>
          </Descriptions>

          <Divider />

          <Title level={4}>Voorraadtelling (Stock Count) naar Exact Online</Title>
          <Descriptions bordered column={1} size="small">
            <Descriptions.Item label="Trigger">
              Bombyxx klikt op '🔢 Voorraadtelling verwerken' in de 'Verwerk Ontvangst' modal
            </Descriptions.Item>
            <Descriptions.Item label="Initiator">
              CMS (direct) → Java Spring Boot API → Exact Online. Niet via Cloud Function.
            </Descriptions.Item>
            <Descriptions.Item label="API Endpoint">
              POST /api/exact/shipments/{"{shipmentId}"}/stock-count
            </Descriptions.Item>
            <Descriptions.Item label="Body">
              receiptDate + lines (itemCode, orderedAmount, receivedAmount)
            </Descriptions.Item>
            <Descriptions.Item label="Exact Verwerking">
              <ol style={{ marginBottom: 0, paddingLeft: 20 }}>
                <li>StockCount aanmaken in Exact (status draft) met QuantityDifference per artikel</li>
                <li>ProcessStockCount aanroepen om de telling te bevestigen (status confirmed)</li>
                <li>Voorraad wordt verhoogd met ontvangen aantallen — bestaande voorraad niet overschreven</li>
              </ol>
            </Descriptions.Item>
            <Descriptions.Item label="Exact Description">
              "Ontvangst verzending {"{shipmentNumber}"}" — gebruikt het leesbare shipment nummer (opgehaald uit Firestore)
            </Descriptions.Item>
            <Descriptions.Item label="Firestore velden (op shipment)">
              <ul style={{ marginBottom: 0, paddingLeft: 20 }}>
                <li><Text code>exactStockCountId</Text> — Exact GUID van de stock count</li>
                <li><Text code>exactStockCountSyncedAt</Text> — Unix timestamp van synchronisatie</li>
                <li><Text code>exactStockCountStatus</Text> — "synced"</li>
                <li><Text code>receivedQuantities</Text> — ontvangen aantallen per product</li>
                <li><Text code>accountantMailSentAt</Text> — Unix timestamp wanneer boekhouder mail verstuurd</li>
              </ul>
            </Descriptions.Item>
            <Descriptions.Item label="Boekhouder Mail">
              Direct na telling: POST /api/exact/shipments/{"{shipmentId}"}/notify-accountant
              → Tijdstip zichtbaar in shipment detail als 'Mail boekhouder verstuurd'
            </Descriptions.Item>
            <Descriptions.Item label="Kostprijs Update">
              Via PUT naar logistics/Items endpoint met veld CostPriceNew (niet via SupplierItem)
            </Descriptions.Item>
          </Descriptions>
        </Panel>

        {/* Cloud Functions */}
        <Panel
          header={
            <Space>
              <ThunderboltOutlined />
              <strong>9. Cloud Functions (Automatische Triggers)</strong>
            </Space>
          }
          key="9"
        >
          <Alert
            message="Firebase Cloud Functions"
            description="Cloud Functions draaien automatisch op de achtergrond wanneer data in Firestore wijzigt. Ze verzorgen email notificaties, Exact Online synchronisatie, en voorraad updates."
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
          />

          {cloudFunctions.map((cf, index) => (
            <Card
              key={index}
              size="small"
              style={{ marginBottom: 16 }}
              title={
                <Space>
                  <Tag color="blue">{cf.event}</Tag>
                  <Text strong>{cf.name}</Text>
                  <Text type="secondary">→ {cf.collection}</Text>
                </Space>
              }
            >
              <Paragraph>{cf.description}</Paragraph>
              <Text strong>Acties:</Text>
              <ul>
                {cf.actions.map((action, idx) => (
                  <li key={idx}>{action}</li>
                ))}
              </ul>
            </Card>
          ))}

          <Divider />

          <Title level={4}>Complete Flow Diagram</Title>
          <Timeline>
            <Timeline.Item color="blue">
              <strong>1. Purchase Order → status: in_production</strong>
              <br />
              Cloud Function: Exact sync (order aanmaken in Exact)
            </Timeline.Item>
            <Timeline.Item color="orange">
              <strong>2. Shipment aangemaakt → status: shipped</strong>
              <br />
              Cloud Function: Email naar Bombyxx
            </Timeline.Item>
            <Timeline.Item color="cyan">
              <strong>3. Shipment → status: received</strong>
              <br />
              Cloud Function: Email naar leverancier + Bombyxx
            </Timeline.Item>
            <Timeline.Item color="green">
              <strong>4. CMS: Voorraadtelling invullen → Shipment status: completed</strong>
              <br />
              CMS → API: Stock Count aanmaken + verwerken in Exact (QuantityDifference) + boekhouder email
              <br />
              Cloud Function: Email notificatie naar leverancier + Bombyxx
            </Timeline.Item>
            <Timeline.Item color="purple">
              <strong>5. Invoice aangemaakt (gekoppeld aan shipment)</strong>
              <br />
              Cloud Function: Exact invoice sync + email naar Bombyxx
            </Timeline.Item>
            <Timeline.Item color="green">
              <strong>6. Invoice → status: paid</strong>
              <br />
              Cloud Function: Email naar leverancier
            </Timeline.Item>
          </Timeline>

          <Divider />

          <Title level={4}>Bestanden</Title>
          <Descriptions bordered column={1} size="small">
            <Descriptions.Item label="Development">
              cloud-functions/functions/projects/bombyxx-dev.js
            </Descriptions.Item>
            <Descriptions.Item label="Production">
              cloud-functions/functions/projects/bombyxx.js
            </Descriptions.Item>
            <Descriptions.Item label="Email Queue">
              emailQueue collectie in Firestore - emails worden binnen 1 minuut verwerkt
            </Descriptions.Item>
            <Descriptions.Item label="Error Handling">
              Fouten worden gelogd naar Slack via sendExceptionToSlack()
            </Descriptions.Item>
          </Descriptions>
        </Panel>

        {/* System Architecture */}
        <Panel
          header={
            <Space>
              <InfoCircleOutlined />
              <strong>10. Systeem Architectuur</strong>
            </Space>
          }
          key="10"
        >
          <Title level={4}>Componenten</Title>
          <Descriptions bordered column={1} size="small">
            <Descriptions.Item label="CMS (Bombyxx)">
              React applicatie voor Bombyxx medewerkers om orders te beheren
            </Descriptions.Item>
            <Descriptions.Item label="Supplier Portal">
              Next.js applicatie voor leveranciers om orders te bekijken en bewerken
            </Descriptions.Item>
            <Descriptions.Item label="API (Java Spring Boot)">
              Backend API voor Exact Online integratie en email verzending
            </Descriptions.Item>
            <Descriptions.Item label="Firestore">
              Database voor alle purchase order data, products, suppliers, etc.
            </Descriptions.Item>
            <Descriptions.Item label="Cloud Functions">
              Serverless functions voor automatische email verzending, Exact Online synchronisatie, goods receipt verwerking en voorraad updates. Triggers op: purchaseOrders, purchaseOrderShipments, purchaseOrderInvoices
            </Descriptions.Item>
            <Descriptions.Item label="Firebase Storage">
              Opslag voor invoice PDFs, product images, en order attachments
            </Descriptions.Item>
            <Descriptions.Item label="Exact Online">
              ERP systeem voor financiële administratie en voorraad beheer
            </Descriptions.Item>
          </Descriptions>

          <Divider />

          <Title level={4}>Firestore Collections</Title>
          <Descriptions bordered column={1} size="small">
            <Descriptions.Item label="purchaseOrders">
              Alle purchase orders met producten, status, leverancier koppeling, etc.
            </Descriptions.Item>
            <Descriptions.Item label="purchaseOrderShipments">
              Verzendingen - losgekoppeld van PO's, kan producten uit meerdere PO's bevatten. Bevat items, receivedQuantities, sourceOrders
            </Descriptions.Item>
            <Descriptions.Item label="purchaseOrderInvoices">
              Facturen - gekoppeld aan een shipment (via shipmentId). Bevat invoice lines, additional costs, status, Exact sync data
            </Descriptions.Item>
            <Descriptions.Item label="purchaseOrderPayments">
              Betalingen - gekoppeld aan een invoice (via invoiceId). Bevat amount, date, method, reference, proof
            </Descriptions.Item>
            <Descriptions.Item label="products">Product master data (catalog)</Descriptions.Item>
            <Descriptions.Item label="suppliers">Leverancier informatie en authenticatie</Descriptions.Item>
            <Descriptions.Item label="productDrafts">
              Nieuwe producten die nog niet in de catalog staan (goedkeuringsproces)
            </Descriptions.Item>
            <Descriptions.Item label="emailQueue">Queue voor automatische email verzending (verwerkt door Cloud Functions)</Descriptions.Item>
            <Descriptions.Item label="config">Systeem configuratie (order counter, shipment counter, etc.)</Descriptions.Item>
          </Descriptions>

          <Divider />

          <Title level={4}>Data Flow</Title>
          <Timeline>
            <Timeline.Item color="blue">
              <strong>1. Order Creation (CMS)</strong>
              <br />
              Bombyxx maakt order aan → Firestore purchaseOrders collection
            </Timeline.Item>
            <Timeline.Item color="green">
              <strong>2. PO Status Change → Cloud Function</strong>
              <br />
              Status update → onUpdatePurchaseOrder → Email queue + Exact sync (bij in_production)
            </Timeline.Item>
            <Timeline.Item color="orange">
              <strong>3. Shipment Created (Supplier Portal)</strong>
              <br />
              Leverancier maakt verzending aan → purchaseOrderShipments collection
            </Timeline.Item>
            <Timeline.Item color="cyan">
              <strong>4. Shipment Status Change → Cloud Function</strong>
              <br />
              Status update → onUpdatePurchaseOrderShipment → Emails + Goods Receipt naar Exact (bij completed)
            </Timeline.Item>
            <Timeline.Item color="purple">
              <strong>5. Invoice Created → Cloud Function</strong>
              <br />
              Leverancier maakt factuur aan → onCreatePurchaseOrderInvoice → Exact invoice sync + email naar Bombyxx
            </Timeline.Item>
            <Timeline.Item color="green">
              <strong>6. Invoice Status Change → Cloud Function</strong>
              <br />
              Status update (approved/paid) → onUpdatePurchaseOrderInvoice → Email naar leverancier
            </Timeline.Item>
            <Timeline.Item color="gray">
              <strong>7. Email Processing</strong>
              <br />
              Cloud Function checkt emailQueue → API MarketingService → Email verzonden
            </Timeline.Item>
          </Timeline>
        </Panel>

        {/* Troubleshooting */}
        <Panel
          header={
            <Space>
              <ExclamationCircleOutlined />
              <strong>11. Troubleshooting & FAQ</strong>
            </Space>
          }
          key="11"
        >
          <Title level={4}>Veelvoorkomende Problemen</Title>

          <Collapse ghost>
            <Panel header="Email wordt niet verzonden" key="8-1">
              <Paragraph>
                <Text strong>Mogelijke oorzaken:</Text>
              </Paragraph>
              <ul>
                <li>Cloud Function is niet actief</li>
                <li>Email queue entry heeft status 'failed'</li>
                <li>API MarketingService is down</li>
                <li>Email adres is incorrect</li>
              </ul>
              <Paragraph>
                <Text strong>Oplossing:</Text>
              </Paragraph>
              <ul>
                <li>Check Firestore emailQueue collection voor status</li>
                <li>Check Cloud Function logs in Firebase Console</li>
                <li>Check API logs voor errors</li>
                <li>Verifieer email adres in supplier/user data</li>
              </ul>
            </Panel>

            <Panel header="Invoice wordt niet gesynchroniseerd naar Exact" key="8-2">
              <Paragraph>
                <Text strong>Mogelijke oorzaken:</Text>
              </Paragraph>
              <ul>
                <li>Exact API credentials zijn verlopen</li>
                <li>Cloud Function is niet getriggered</li>
                <li>Invoice data is incompleet</li>
              </ul>
              <Paragraph>
                <Text strong>Oplossing:</Text>
              </Paragraph>
              <ul>
                <li>Check Exact API token in API configuratie</li>
                <li>Check Cloud Function logs</li>
                <li>Verifieer invoice heeft alle verplichte velden</li>
                <li>Check exactPurchaseInvoiceId in Firestore</li>
              </ul>
            </Panel>

            <Panel header="Order split werkt niet" key="8-3">
              <Paragraph>
                <Text strong>Mogelijke oorzaken:</Text>
              </Paragraph>
              <ul>
                <li>Order is niet in 'In Production' status</li>
                <li>Quantities tellen niet op tot origineel</li>
                <li>ETD ontbreekt</li>
              </ul>
              <Paragraph>
                <Text strong>Oplossing:</Text>
              </Paragraph>
              <ul>
                <li>Verifieer order status is 'In Production'</li>
                <li>Check dat split quantities exact optellen tot origineel</li>
                <li>Vul alle verplichte velden in (ETD Now, ETD Later)</li>
              </ul>
            </Panel>

            <Panel header="Product master data wordt niet geüpdatet" key="8-4">
              <Paragraph>
                <Text strong>Mogelijke oorzaken:</Text>
              </Paragraph>
              <ul>
                <li>Product heeft geen productId (is een draft product)</li>
                <li>Waarden zijn niet gewijzigd</li>
                <li>updateProductPricesAndPackaging functie wordt niet aangeroepen</li>
              </ul>
              <Paragraph>
                <Text strong>Oplossing:</Text>
              </Paragraph>
              <ul>
                <li>Check dat product een productId heeft (geen productDraftId)</li>
                <li>Verifieer dat adjusted values verschillen van originele values</li>
                <li>Check CMS logs voor errors in updateProductPricesAndPackaging</li>
              </ul>
            </Panel>

            <Panel header="Additional costs email wordt niet verzonden" key="8-5">
              <Paragraph>
                <Text strong>Mogelijke oorzaken:</Text>
              </Paragraph>
              <ul>
                <li>additionalCostsNotificationPending flag is niet gezet</li>
                <li>Cloud Function onUpdatePurchaseOrder is niet getriggered</li>
                <li>Email type is niet toegevoegd aan MarketingService</li>
              </ul>
              <Paragraph>
                <Text strong>Oplossing:</Text>
              </Paragraph>
              <ul>
                <li>Check Firestore order voor additionalCostsNotificationPending flag</li>
                <li>Check Cloud Function logs</li>
                <li>Verifieer MarketingService heeft 'purchase_order_additional_costs_added' case</li>
              </ul>
            </Panel>

            <Panel header="Goods Receipt wordt niet verwerkt in Exact" key="8-6">
              <Paragraph>
                <Text strong>Mogelijke oorzaken:</Text>
              </Paragraph>
              <ul>
                <li>Shipment heeft geen receivedQuantities (telling niet ingevuld)</li>
                <li>Shipment heeft geen sourceOrders met purchaseOrderNumber</li>
                <li>Cloud Function onUpdatePurchaseOrderShipment is niet getriggered</li>
                <li>Exact API credentials zijn verlopen</li>
              </ul>
              <Paragraph>
                <Text strong>Oplossing:</Text>
              </Paragraph>
              <ul>
                <li>Check Firestore shipment document voor receivedQuantities array</li>
                <li>Verifieer sourceOrders[0].purchaseOrderNumber bestaat</li>
                <li>Check Cloud Function logs voor onUpdatePurchaseOrderShipment</li>
                <li>Check Exact API token status</li>
              </ul>
            </Panel>

            <Panel header="Shipment emails worden niet verzonden" key="8-7">
              <Paragraph>
                <Text strong>Mogelijke oorzaken:</Text>
              </Paragraph>
              <ul>
                <li>Shipment heeft geen supplierId (supplier lookup faalt)</li>
                <li>Cloud Function onUpdatePurchaseOrderShipment is niet actief</li>
                <li>Status change is niet gedetecteerd (before.status === after.status)</li>
              </ul>
              <Paragraph>
                <Text strong>Oplossing:</Text>
              </Paragraph>
              <ul>
                <li>Verifieer supplierId op het shipment document</li>
                <li>Check Cloud Function deployment status</li>
                <li>Check Firestore emailQueue voor shipment-gerelateerde entries</li>
              </ul>
            </Panel>
          </Collapse>

          <Divider />

          <Title level={4}>Belangrijke Notities</Title>
          <Alert
            message="Status vs Payment Status"
            description="Order status en payment status zijn gescheiden. Een order kan 'Ready for Production' zijn maar nog 'Payment Pending' als payment status."
            type="warning"
            showIcon
            style={{ marginBottom: 8 }}
          />
          <Alert
            message="Invoice Total Price"
            description="Total Price in overzichtstabel toont altijd producten + additional costs. In detail pagina wordt breakdown getoond."
            type="info"
            showIcon
            style={{ marginBottom: 8 }}
          />
          <Alert
            message="Order Splitting"
            description="Na split blijft originele order zichtbaar met status 'Split'. Child orders krijgen suffix -A en -B."
            type="info"
            showIcon
            style={{ marginBottom: 8 }}
          />
          <Alert
            message="Email Delays"
            description="Emails worden binnen 1 minuut verzonden na trigger. Check emailQueue collection voor status."
            type="info"
            showIcon
          />
        </Panel>
      </Collapse>
              </>
            ),
          },
          {
            key: "marketing",
            label: (
              <span>
                <MailOutlined /> Marketing
              </span>
            ),
            children: (
              <>
                <Paragraph>
                  Documentatie voor marketing email campagnes en tijdelijke klant instellingen.
                </Paragraph>
                <Collapse activeKey={activeKey} onChange={setActiveKey}>
        {/* Marketing Emails */}
        <Panel
          header={
            <Space>
              <MailOutlined />
              <strong>1. Marketing Email Campagnes</strong>
            </Space>
          }
          key="marketing-1"
        >
          <Alert
            message="Dynamische Marketing Campagnes"
            description="Verstuur gepersonaliseerde marketing emails naar klanten met tijdelijke instellingen die automatisch verlopen aan het einde van de dag."
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
          />

          <Title level={4}>Beschikbare Campagne Types</Title>
          <Descriptions bordered column={1} size="small" style={{ marginBottom: 16 }}>
            <Descriptions.Item label="🎁 Gratis Verzending €750">
              Alleen vandaag gratis verzending bij bestellingen vanaf €750
            </Descriptions.Item>
            <Descriptions.Item label="💰 Bestellen vanaf €500">
              Alleen vandaag bestellen vanaf €500 (verlaagde minimale bestelwaarde)
            </Descriptions.Item>
            <Descriptions.Item label="🎉 5% Korting">
              Alleen vandaag 5% korting bij bestellingen boven [dynamisch bedrag]
            </Descriptions.Item>
          </Descriptions>

          <Divider />

          <Title level={4}>Hoe te Gebruiken</Title>
          <Timeline>
            <Timeline.Item color="blue">
              <Title level={5}>1. Open Customer Drawer</Title>
              <Paragraph>
                Ga naar Klanten pagina en klik op een klant om de Customer Drawer te openen
              </Paragraph>
            </Timeline.Item>
            <Timeline.Item color="blue">
              <Title level={5}>2. Klik op "Verstuur Marketing Email"</Title>
              <Paragraph>
                In de Customer Drawer, scroll naar de "Marketing Emails" sectie en klik op de knop
              </Paragraph>
            </Timeline.Item>
            <Timeline.Item color="blue">
              <Title level={5}>3. Selecteer Campagne Type</Title>
              <Paragraph>
                Kies een van de 3 beschikbare campagne types. De modal toont automatisch:
              </Paragraph>
              <ul>
                <li>Huidige winkelwagen waarde van de klant</li>
                <li>Berekende drempel voor 5% korting campagne (afgerond naar eerstvolgende duizendtal)</li>
                <li>Waarschuwing als dezelfde email recent is verstuurd (&lt; 7 dagen)</li>
              </ul>
            </Timeline.Item>
            <Timeline.Item color="blue">
              <Title level={5}>4. Verstuur Email</Title>
              <Paragraph>
                Klik op "Versturen" om de email te verzenden. De email wordt direct verstuurd en:
              </Paragraph>
              <ul>
                <li>Tijdelijke instellingen worden geactiveerd voor de klant</li>
                <li>Email wordt opgeslagen in de historiek</li>
                <li>Instellingen verlopen automatisch aan het einde van de dag</li>
              </ul>
            </Timeline.Item>
          </Timeline>

          <Divider />

          <Title level={4}>Dynamische Drempel Berekening (5% Korting)</Title>
          <Card size="small" style={{ backgroundColor: "#f0f5ff", marginBottom: 16 }}>
            <Paragraph>
              <Text strong>Automatische Berekening:</Text>
            </Paragraph>
            <ul>
              <li>Winkelwagen: €3.300 → Drempel: €4.000</li>
              <li>Winkelwagen: €2.800 → Drempel: €3.000</li>
              <li>Winkelwagen: €5.100 → Drempel: €6.000</li>
            </ul>
            <Paragraph>
              De drempel wordt automatisch afgerond naar het eerstvolgende duizendtal om een psychologisch aantrekkelijk bedrag te creëren.
            </Paragraph>
          </Card>

          <Divider />

          <Title level={4}>Tijdelijke Instellingen</Title>
          <Descriptions bordered column={1} size="small" style={{ marginBottom: 16 }}>
            <Descriptions.Item label="Gratis Verzending €750">
              <ul style={{ marginBottom: 0, paddingLeft: 20 }}>
                <li>freeShippingUntil: einde van vandaag</li>
                <li>freeShippingThreshold: €750</li>
              </ul>
            </Descriptions.Item>
            <Descriptions.Item label="Bestellen vanaf €500">
              <ul style={{ marginBottom: 0, paddingLeft: 20 }}>
                <li>minimumOrderOverrideUntil: einde van vandaag</li>
                <li>minimumOrderAmount: €500</li>
              </ul>
            </Descriptions.Item>
            <Descriptions.Item label="5% Korting">
              <ul style={{ marginBottom: 0, paddingLeft: 20 }}>
                <li>discountPercentageUntil: einde van vandaag</li>
                <li>discountPercentage: 5%</li>
                <li>discountMinimumAmount: [berekende drempel]</li>
              </ul>
            </Descriptions.Item>
          </Descriptions>

          <Alert
            message="Automatisch Verlopen"
            description="Alle tijdelijke instellingen verlopen automatisch aan het einde van de dag (23:59:59). Er is geen handmatige actie nodig."
            type="success"
            showIcon
            style={{ marginBottom: 16 }}
          />

          <Divider />

          <Title level={4}>Email Historiek</Title>
          <Paragraph>
            In de Customer Drawer wordt een overzicht getoond van alle verstuurde marketing emails:
          </Paragraph>
          <ul>
            <li>Type campagne met icoon en kleur</li>
            <li>Datum en tijd van verzending</li>
            <li>Winkelwagen waarde op moment van verzenden</li>
            <li>Berekende drempel (voor 5% korting campagne)</li>
            <li>Waarschuwing als email recent is verstuurd (&lt; 7 dagen geleden)</li>
          </ul>

          <Divider />

          <Title level={4}>Database Structuur</Title>
          <Descriptions bordered column={1} size="small" style={{ marginBottom: 16 }}>
            <Descriptions.Item label="Email Historiek">
              /users/{"{userId}"}/marketingEmails/{"{emailId}"}
            </Descriptions.Item>
            <Descriptions.Item label="Tijdelijke Instellingen">
              /users/{"{userId}"} → temporarySettings object
            </Descriptions.Item>
            <Descriptions.Item label="Email Queue">
              Gebruikt bestaande emailQueue collectie voor verzending
            </Descriptions.Item>
          </Descriptions>

          <Divider />

          <Title level={4}>Webshop Integratie</Title>
          <Alert
            message="Checkout Validatie"
            description="De webshop checkout moet de temporarySettings van de gebruiker controleren en de juiste kortingen/gratis verzending toepassen."
            type="warning"
            showIcon
            style={{ marginBottom: 16 }}
          />
          <Paragraph>
            <Text strong>Te implementeren in checkout:</Text>
          </Paragraph>
          <ul>
            <li>Check user.temporarySettings bij het laden van checkout</li>
            <li>Valideer expiresAt timestamp (moet in de toekomst liggen)</li>
            <li>Toon banner met actieve aanbieding</li>
            <li>Pas gratis verzending toe als freeShippingUntil actief is</li>
            <li>Verlaag minimum order amount als minimumOrderOverrideUntil actief is</li>
            <li>Pas korting toe als discountPercentageUntil actief is en order boven drempel</li>
          </ul>

          <Divider />

          <Title level={4}>Best Practices</Title>
          <Alert
            message="Frequentie Waarschuwing"
            description="Het systeem waarschuwt automatisch als dezelfde email binnen 7 dagen is verstuurd. Dit voorkomt email fatigue bij klanten."
            type="info"
            showIcon
            style={{ marginBottom: 8 }}
          />
          <Alert
            message="Rate Limiting"
            description="Er is een delay van 40 seconden tussen het versturen van emails om email server limits te respecteren (max 10 emails per 5 minuten)."
            type="info"
            showIcon
          />
        </Panel>
      </Collapse>
              </>
            ),
          },
        ]}
      />
    </Card>
  );
};

export default Documentation;
